import { useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { Button } from '@frontend/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@frontend/components/ui/card';
import { Input } from '@frontend/components/ui/input';
import { Label } from '@frontend/components/ui/label';

type Step = 'email' | 'otp';

export function LoginPage() {
  const { signIn, confirmSignIn, error: authError } = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const error = localError || authError;

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setLocalError('Please enter your email address.');
      return;
    }

    setIsLoading(true);

    try {
      await signIn(trimmedEmail);
      setStep('otp');
    } catch {
      // Error is set by AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const trimmedCode = otpCode.trim();

    if (!trimmedCode || trimmedCode.length < 6) {
      setLocalError('Please enter the verification code.');
      return;
    }

    setIsLoading(true);

    try {
      await confirmSignIn(trimmedCode);
    } catch {
      // Error is set by AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setOtpCode('');
    setLocalError(null);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <Card className="w-[400px]">
        <CardHeader>
          <CardTitle>Welcome to EventsDC Documents</CardTitle>
          <CardDescription>
            {step === 'email'
              ? 'Enter your email to receive a verification code'
              : `A verification code has been sent to ${email}`}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

          {step === 'email' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Sending code...' : 'Send Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  placeholder="00000000"
                  value={otpCode}
                  onChange={(e) =>
                    setOtpCode(e.target.value.replace(/\D/g, ''))
                  }
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </Button>
            </form>
          )}
        </CardContent>

        <CardFooter className="text-xs text-center text-gray-500">
          Access is restricted to authorized users only.
        </CardFooter>
      </Card>
    </div>
  );
}
